import { useEffect, useMemo, useState } from "react";
import { Filter, X } from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetFooter,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { TYPE_LABEL } from "@/components/AcornPlaceholder";

const TYPES = ["rosso", "bianco", "rosato", "bollicine", "dolce"];

const EMPTY = {
    tipo: "",
    paese: "",
    regione: "",
    produttore: "",
    annata: "",
    formato: "",
    calice: false,
    prezzo_min: "",
    prezzo_max: "",
    vitigni: [],
};

/**
 * FiltersBar (faceted).
 *
 * Props:
 *   - value: stato filtri corrente
 *   - onChange: callback (nextFilters)
 *   - showPriceFilter: bool
 *   - facets: risposta di GET /api/wines/facets calcolata dal parent col
 *     medesimo stato filtri. Ogni dimensione è una lista `[{value,count}]`
 *     che rappresenta i valori disponibili DATO lo stato corrente (cross-facet).
 *     Passa `null` finché non è stato caricato: la barra mostra un placeholder.
 *   - total: conteggio totale corrente (usato per il pulsante Applica).
 */
export default function FiltersBar({ value, onChange, showPriceFilter, facets, total }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value);

    useEffect(() => {
        setDraft({ ...EMPTY, ...value, vitigni: value.vitigni || [] });
    }, [value]);

    const activeCount = Object.entries(value).reduce((n, [k, v]) => {
        if (k === "calice") return v ? n + 1 : n;
        if (k === "vitigni") return (Array.isArray(v) && v.length > 0) ? n + 1 : n;
        return v !== "" && v !== null && v !== undefined ? n + 1 : n;
    }, 0);

    const apply = () => {
        onChange(draft);
        setOpen(false);
    };
    const clear = () => {
        const next = { ...EMPTY, vitigni: [] };
        setDraft(next);
        onChange(next);
        setOpen(false);
    };

    const toggleGrape = (g) => {
        const cur = draft.vitigni || [];
        const next = cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g];
        setDraft({ ...draft, vitigni: next });
    };

    // Facet lookup helpers — restituiscono la lista {value,count} per la
    // dimensione, oppure [] se i facet non sono ancora arrivati.
    const f = facets?.facets || {};
    const typeCounts = useMemo(() => Object.fromEntries((f.types || []).map((x) => [x.value, x.count])), [f.types]);
    const countryCounts = useMemo(() => Object.fromEntries((f.countries || []).map((x) => [x.value, x.count])), [f.countries]);
    const regionCounts = useMemo(() => Object.fromEntries((f.regions || []).map((x) => [x.value, x.count])), [f.regions]);
    const producerCounts = useMemo(() => Object.fromEntries((f.producers || []).map((x) => [x.value, x.count])), [f.producers]);
    const vintageCounts = useMemo(() => Object.fromEntries((f.vintages || []).map((x) => [String(x.value), x.count])), [f.vintages]);
    const formatCounts = useMemo(() => Object.fromEntries((f.formats || []).map((x) => [x.value, x.count])), [f.formats]);
    const grapeCounts = useMemo(() => Object.fromEntries((f.grapes || []).map((x) => [x.value, x.count])), [f.grapes]);
    const caliceCounts = f.calice || {};

    return (
        <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <button className="co-btn-ghost text-sm" data-testid="filters-open-btn">
                        <Filter className="w-4 h-4" strokeWidth={1.5} />
                        Filtra
                        {activeCount > 0 && (
                            <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px]">
                                {activeCount}
                            </span>
                        )}
                    </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[hsl(var(--background))]">
                    <SheetHeader className="text-left">
                        <SheetTitle className="text-2xl">Filtra la carta</SheetTitle>
                        <SheetDescription className="text-[hsl(var(--muted-foreground))]">
                            {facets
                                ? <>Selezione corrente: <b className="text-[hsl(var(--foreground))]">{total ?? 0}</b> etichett{(total === 1) ? "a" : "e"}.</>
                                : "Caricamento opzioni…"}
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-6 space-y-5">
                        <FacetedSelect
                            label="Tipologia" testId="filter-tipo"
                            value={draft.tipo}
                            onChange={(v) => setDraft({ ...draft, tipo: v })}
                            options={TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t], count: typeCounts[t] || 0 }))}
                        />
                        <FacetedSelect
                            label="Paese" testId="filter-paese"
                            value={draft.paese}
                            onChange={(v) => setDraft({ ...draft, paese: v })}
                            options={(f.countries || []).map((c) => ({ value: c.value, label: c.value, count: c.count }))}
                            selectedCount={draft.paese ? (countryCounts[draft.paese] ?? 0) : undefined}
                        />
                        <FacetedSelect
                            label="Zona / Regione" testId="filter-regione"
                            value={draft.regione}
                            onChange={(v) => setDraft({ ...draft, regione: v })}
                            options={(f.regions || []).map((r) => ({ value: r.value, label: r.value, count: r.count }))}
                            selectedCount={draft.regione ? (regionCounts[draft.regione] ?? 0) : undefined}
                        />
                        <FacetedSelect
                            label="Produttore" testId="filter-produttore"
                            value={draft.produttore}
                            onChange={(v) => setDraft({ ...draft, produttore: v })}
                            options={(f.producers || []).map((p) => ({ value: p.value, label: p.value, count: p.count }))}
                            selectedCount={draft.produttore ? (producerCounts[draft.produttore] ?? 0) : undefined}
                        />
                        <FacetedSelect
                            label="Annata" testId="filter-annata"
                            value={draft.annata}
                            onChange={(v) => setDraft({ ...draft, annata: v })}
                            options={(f.vintages || []).map((y) => ({ value: String(y.value), label: String(y.value), count: y.count }))}
                            selectedCount={draft.annata ? (vintageCounts[String(draft.annata)] ?? 0) : undefined}
                        />
                        <FacetedSelect
                            label="Formato bottiglia" testId="filter-formato"
                            value={draft.formato}
                            onChange={(v) => setDraft({ ...draft, formato: v })}
                            options={(f.formats || []).map((fm) => ({ value: fm.value, label: `${fm.value} L`, count: fm.count }))}
                            selectedCount={draft.formato ? (formatCounts[draft.formato] ?? 0) : undefined}
                        />

                        <div>
                            <Label className="co-caption mb-2 block">
                                Uvaggio {draft.vitigni.length > 0 && <span className="text-[hsl(var(--primary))] normal-case tracking-normal">· {draft.vitigni.length} selezionat{draft.vitigni.length === 1 ? "o" : "i"}</span>}
                            </Label>
                            <div className="border border-[hsl(var(--border))] rounded-sm bg-white max-h-52 overflow-y-auto p-2 space-y-1" data-testid="filter-vitigni-list">
                                {(f.grapes || []).length === 0 ? (
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] italic p-2">
                                        {facets ? "Nessun vitigno per il set corrente." : "Caricamento…"}
                                    </div>
                                ) : (
                                    (f.grapes || []).map((g) => {
                                        const checked = draft.vitigni.includes(g.value);
                                        const disabled = !checked && g.count === 0;
                                        return (
                                            <label
                                                key={g.value}
                                                className={`flex items-center gap-2 px-2 py-1 hover:bg-[hsl(var(--secondary))] text-sm rounded-sm ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => !disabled && toggleGrape(g.value)}
                                                    disabled={disabled}
                                                    data-testid={`filter-vitigno-${g.value}`}
                                                />
                                                <span className="flex-1">{g.value}</span>
                                                <span className="text-[10px] text-[hsl(var(--muted-foreground))]" data-testid={`filter-vitigno-count-${g.value}`}>
                                                    {g.count}
                                                </span>
                                            </label>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-3 border border-[hsl(var(--border))] rounded-sm bg-white">
                            <Label className="text-sm cursor-pointer flex-1" htmlFor="filter-calice-switch">
                                Disponibile al calice
                                {caliceCounts.true !== undefined && (
                                    <span className="ml-2 text-[10px] text-[hsl(var(--muted-foreground))]" data-testid="filter-calice-count">
                                        ({caliceCounts.true})
                                    </span>
                                )}
                            </Label>
                            <Switch
                                id="filter-calice-switch"
                                data-testid="filter-calice-switch"
                                checked={draft.calice}
                                onCheckedChange={(v) => setDraft({ ...draft, calice: v })}
                                disabled={!draft.calice && (caliceCounts.true === 0)}
                            />
                        </div>

                        {showPriceFilter && (
                            <div data-testid="filter-price-block">
                                <Label className="co-caption mb-2 block">Fascia di prezzo (€)</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number" placeholder="Min" min="0" step="1"
                                        value={draft.prezzo_min}
                                        data-testid="filter-prezzo-min"
                                        onChange={(e) => setDraft({ ...draft, prezzo_min: e.target.value })}
                                        className="bg-white"
                                    />
                                    <Input
                                        type="number" placeholder="Max" min="0" step="1"
                                        value={draft.prezzo_max}
                                        data-testid="filter-prezzo-max"
                                        onChange={(e) => setDraft({ ...draft, prezzo_max: e.target.value })}
                                        className="bg-white"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <SheetFooter className="mt-8 flex-row gap-3">
                        <button className="co-btn-ghost flex-1" onClick={clear} data-testid="filters-clear-btn">
                            <X className="w-4 h-4" strokeWidth={1.5} /> Azzera
                        </button>
                        <button className="co-btn-primary flex-1" onClick={apply} data-testid="filters-apply-btn">
                            Applica
                        </button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {activeCount > 0 && (
                <button
                    className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors"
                    onClick={() => onChange({ ...EMPTY, vitigni: [] })}
                    data-testid="filters-inline-clear"
                >
                    Azzera filtri
                </button>
            )}
        </div>
    );
}

/**
 * Select faceted: nasconde le opzioni con count 0 (a meno che siano
 * quella selezionata), mostra i conteggi accanto a ciascuna opzione.
 *
 * Props:
 *   - options: [{ value, label, count }]
 *   - value: valore corrente ("" = tutti)
 *   - selectedCount: se il valore selezionato non appare nelle options
 *     (perché count=0), lo aggiungiamo comunque come opzione mantenuta
 *     con `selectedCount` come suffix.
 */
function FacetedSelect({ label, value, onChange, options, testId, selectedCount }) {
    // Se il value corrente non è tra le options con count>0, aggiungilo
    // artificialmente per non "perdere" la selezione dell'utente.
    const filtered = (options || []).filter((o) => o.count > 0 || o.value === value);
    const hasSelected = filtered.some((o) => o.value === value);
    const finalOpts = hasSelected || !value
        ? filtered
        : [...filtered, { value, label: value, count: selectedCount ?? 0 }];

    return (
        <div>
            <Label className="co-caption mb-2 block">{label}</Label>
            <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
                <SelectTrigger data-testid={testId} className="bg-white">
                    <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                    <SelectItem value="__all">Tutti</SelectItem>
                    {finalOpts.length === 0 && (
                        <div className="px-2 py-1.5 text-xs text-[hsl(var(--muted-foreground))] italic">
                            Nessuna opzione compatibile
                        </div>
                    )}
                    {finalOpts.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            <span className="flex items-center justify-between gap-4 w-full">
                                <span>{o.label}</span>
                                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                                    {o.count}
                                </span>
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

export { EMPTY as EMPTY_FILTERS };
